import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { CheckCircle2, AlertCircle } from "lucide-react";
import { useAuth } from "@/stores/authStore";
import { apiError } from "@/services/api";
import { InlineLanguageSelector } from "@/components/InlineLanguageSelector";
import { Button, Input, Label, Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components";
import api from "@/services/api";
import { cn } from "@/lib/utils";

const EMPTY = {
  company_name: "", org_number: "", vat_number: "", address: "", city: "",
  country: "Sverige", phone: "", website: "", name: "", email: "", password: "", confirm_password: "", industry: "",
};

// Field-level validators — return a validation KEY (i18n) or "" when valid.
const RULES = {
  industry: (v) => (v ? "" : "industry"),
  company_name: (v) => (v.trim().length < 2 ? "company_name" : ""),
  org_number: (v) => (/^\d{6}-?\d{4}$/.test(v.trim()) ? "" : "org_number"),
  vat_number: (v) => (!v.trim() ? "" : /^SE\d{12}$/i.test(v.trim()) ? "" : "vat_number"),
  address: (v) => (v.trim().length < 2 ? "address" : ""),
  city: (v) => (v.trim().length < 2 ? "city" : ""),
  country: (v) => (v.trim().length < 2 ? "country" : ""),
  phone: (v) => (/^[+\d][\d\s\-()]{5,}$/.test(v.trim()) ? "" : "phone"),
  website: () => "",
  name: (v) => (v.trim().length < 2 ? "name" : ""),
  email: (v) => (/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(v.trim()) ? "" : "email"),
  password: (v) =>
    v.length < 8 ? "passwordMin" : !/[A-Za-z]/.test(v) || !/\d/.test(v) ? "passwordComplex" : "",
};

function Field({ id, label, type = "text", placeholder, required = true, half, value, onChange, onBlur, errorKey, touched, optionalLabel, t }) {
  const invalid = touched && !!errorKey;
  const valid = touched && !errorKey && (required || value);
  return (
    <div className={`flex flex-col gap-2 ${half ? "" : "sm:col-span-2"}`}>
      <Label htmlFor={id}>{label}{!required && <span className="text-muted-foreground"> ({optionalLabel})</span>}</Label>
      <div className="relative">
        <Input
          id={id} type={type} value={value} onChange={onChange} onBlur={onBlur} placeholder={placeholder}
          data-testid={`register-${id}`} aria-invalid={invalid} aria-describedby={invalid ? `${id}-error` : undefined}
          className={cn("pr-9", invalid && "border-destructive focus-visible:ring-destructive", valid && "border-emerald-500/60")}
        />
        {invalid && <AlertCircle className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-destructive" />}
        {valid && !invalid && <CheckCircle2 className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-emerald-500" />}
      </div>
      {invalid && (
        <p id={`${id}-error`} data-testid={`register-${id}-error`} className="flex items-center gap-1 text-xs text-destructive">
          {t(`validation.${errorKey}`)}
        </p>
      )}
    </div>
  );
}

export function RegisterForm() {
  const { t, i18n } = useTranslation();
  const lang = (i18n.language || "sv").slice(0, 2);
  const [industries, setIndustries] = useState([]);
  useEffect(() => { api.get("/public/industries").then((r) => setIndustries(r.data.data || [])).catch(() => setIndustries([{ key: "it_repair", label: { sv: "IT-service & reparation" } }])); }, []);
  const { register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [loading, setLoading] = useState(false);

  const validate = (id, val) => {
    if (id === "confirm_password") return val !== form.password ? "passwordMismatch" : "";
    const rule = RULES[id];
    return rule ? rule(val) : "";
  };

  const set = (id) => (e) => {
    const val = e.target.value;
    setForm((f) => {
      const next = { ...f, [id]: val };
      setErrors((prev) => ({
        ...prev,
        [id]: validate(id, val),
        ...(id === "password" && touched.confirm_password
          ? { confirm_password: next.confirm_password !== val ? "passwordMismatch" : "" }
          : {}),
      }));
      return next;
    });
  };

  const blur = (id) => () => {
    setTouched((tc) => ({ ...tc, [id]: true }));
    setErrors((prev) => ({ ...prev, [id]: validate(id, form[id]) }));
  };

  const runAll = () => {
    const next = {};
    Object.keys(EMPTY).forEach((k) => { next[k] = validate(k, form[k]); });
    setErrors(next);
    setTouched(Object.keys(EMPTY).reduce((a, k) => ((a[k] = true), a), {}));
    return Object.values(next).every((v) => !v);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!runAll()) { toast.error(t("register.checkFields")); return; }
    setLoading(true);
    try {
      const payload = { ...form };
      delete payload.confirm_password;
      if (!payload.vat_number) delete payload.vat_number;
      if (!payload.website) delete payload.website;
      await register(payload);
      toast.success(t("register.success"));
      navigate("/dashboard");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setLoading(false);
    }
  };

  const field = (id, opts = {}) => (
    <Field id={id} t={t}
      label={t(`register.fields.${id}.label`)}
      placeholder={t(`register.fields.${id}.placeholder`)}
      optionalLabel={t("common.optional")}
      value={form[id]} onChange={set(id)} onBlur={blur(id)}
      errorKey={errors[id]} touched={touched[id]} {...opts} />
  );

  return (
    <form onSubmit={submit} className="flex flex-col gap-6" data-testid="register-form" noValidate>
      <InlineLanguageSelector testId="register-language" />
      <div>
        <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-primary">{t("register.companySection")}</p>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div className="flex flex-col gap-1.5 sm:col-span-2">
            <Label htmlFor="reg-industry">{t("register.fields.industry.label")} <span className="text-destructive">*</span></Label>
            <Select value={form.industry} onValueChange={(v) => { setForm((f) => ({ ...f, industry: v })); setErrors((p) => ({ ...p, industry: "" })); }}>
              <SelectTrigger id="reg-industry" data-testid="register-industry" aria-invalid={!!(touched.industry && errors.industry)} className={cn(touched.industry && errors.industry && "border-destructive")}><SelectValue placeholder={t("register.fields.industry.placeholder")} /></SelectTrigger>
              <SelectContent>
                {industries.map((i) => <SelectItem key={i.key} value={i.key} data-testid={`register-industry-${i.key}`}>{i.label[lang] || i.label.sv}</SelectItem>)}
              </SelectContent>
            </Select>
            {touched.industry && errors.industry && (
              <p data-testid="register-industry-error" className="text-xs text-destructive">{t(`validation.${errors.industry}`)}</p>
            )}
            <p className="text-xs text-muted-foreground">{t("register.fields.industry.hint")}</p>
          </div>
          {field("company_name")}
          {field("org_number", { half: true })}
          {field("vat_number", { required: false, half: true })}
          {field("address")}
          {field("city", { half: true })}
          {field("country", { half: true })}
          {field("phone", { half: true })}
          {field("website", { required: false, half: true })}
        </div>
      </div>

      <div>
        <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-primary">{t("register.adminSection")}</p>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          {field("name")}
          {field("email", { type: "email" })}
          {field("password", { type: "password" })}
          {field("confirm_password", { type: "password" })}
        </div>
      </div>

      <Button type="submit" disabled={loading} data-testid="register-submit">
        {loading ? t("register.submitting") : t("register.submit")}
      </Button>
      <p className="text-center text-sm text-muted-foreground">
        {t("register.haveAccount")}{" "}
        <Link to="/login" className="text-primary hover:underline" data-testid="to-login-link">{t("register.login")}</Link>
      </p>
    </form>
  );
}
